import React from 'react';

import { Tag } from '@/design-system/tag';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import {
  codeCls,
  metaCls,
  nameCls,
  numCls,
  StockTypeBadge,
} from '../../components/ui';
import { fmtDate, fmtQty, isExpired } from '../../format';
import type { StockOpnameLine } from '../../types';
import { fmtGap, gapCls } from './opnameDraft';

const COLUMNS: TableDataColumn<StockOpnameLine>[] = [
  {
    key: 'storage',
    title: 'Storage',
    render: (_v, l) => (
      <div className="min-w-0">
        <p className={nameCls}>{l.storageName}</p>
        {l.storagePath && l.storagePath !== l.storageName && (
          <p className={`${metaCls} truncate`}>{l.storagePath}</p>
        )}
      </div>
    ),
  },
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => (
      <div className="min-w-0">
        <p className={codeCls}>{l.sku}</p>
        <p className={metaCls}>{l.itemName}</p>
      </div>
    ),
  },
  {
    key: 'batchNumber',
    title: 'Batch',
    render: (_v, l) => (
      <div className="flex flex-wrap items-center gap-1.5">
        {l.batchNumber ? (
          <span className={numCls}>{l.batchNumber}</span>
        ) : (
          <span className={metaCls}>Dibuat saat disetujui FAT</span>
        )}
        {l.isNewBatch && (
          <Tag size="xs" variant="stroke">
            Batch baru
          </Tag>
        )}
      </div>
    ),
  },
  {
    key: 'stockType',
    title: 'Tipe',
    render: (_v, l) => <StockTypeBadge type={l.stockType} />,
  },
  {
    key: 'expiredDate',
    title: 'Expiry',
    render: (_v, l) =>
      l.expiredDate ? (
        <span
          className={`${metaCls} ${isExpired(l.expiredDate) ? 'text-(--state-error-base)' : ''}`}
        >
          {fmtDate(l.expiredDate)}
          {isExpired(l.expiredDate) ? ' · expired' : ''}
        </span>
      ) : (
        <span className={metaCls}>Tanpa expiry</span>
      ),
  },
  {
    key: 'qtySystem',
    title: 'Sistem',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtySystem)}</span>,
  },
  {
    key: 'qtyPhysical',
    title: 'Fisik',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyPhysical)}</span>,
  },
  {
    key: 'qtyGap',
    title: 'Selisih',
    align: 'right',
    render: (_v, l) => (
      <span
        className={`font-mono text-[12px] font-semibold tabular-nums ${gapCls(l.qtyGap)}`}
      >
        {fmtGap(l.qtyGap)}
      </span>
    ),
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
  {
    key: 'notes',
    title: 'Catatan',
    render: (_v, l) =>
      l.notes ? (
        <span className={`${metaCls} line-clamp-2 max-w-[200px]`}>
          {l.notes}
        </span>
      ) : (
        <span className={metaCls}>—</span>
      ),
  },
];

/** The counted rows of an opname: saved (Detail) or about to be (Review). */
export const OpnameLinesTable: React.FC<{
  lines: StockOpnameLine[];
  testId?: string;
}> = ({ lines, testId }) => (
  <div data-testid={testId}>
    <TableData size="sm" data={lines} columns={COLUMNS} />
  </div>
);
